import React from 'react';
import {
  Box,
  IconButton,
  Select,
  MenuItem,
  Typography,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  FirstPage,
  LastPage,
} from '@mui/icons-material';

interface CompactPaginationProps {
  count: number;
  page: number;
  rowsPerPage: number;
  rowsPerPageOptions: number[];
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const CompactPagination: React.FC<CompactPaginationProps> = ({
  count,
  page,
  rowsPerPage,
  rowsPerPageOptions,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const totalPages = Math.ceil(count / rowsPerPage);
  const startIndex = page * rowsPerPage + 1;
  const endIndex = Math.min((page + 1) * rowsPerPage, count);

  const handleFirstPage = () => {
    onPageChange(null, 0);
  };

  const handlePreviousPage = () => {
    onPageChange(null, page - 1);
  };

  const handleNextPage = () => {
    onPageChange(null, page + 1);
  };

  const handleLastPage = () => {
    onPageChange(null, totalPages - 1);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 16px',
        borderTop: '1px solid rgba(224, 224, 224, 1)',
        backgroundColor: '#f8f9fa',
        minHeight: '40px',
        maxHeight: '40px',
      }}
    >
      {/* Left side - Rows per page */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
          Rows per page:
        </Typography>
        <Select
          value={rowsPerPage}
          onChange={onRowsPerPageChange as any}
          size="small"
          sx={{
            minWidth: '60px',
            height: '32px',
            '& .MuiSelect-select': {
              padding: '4px 8px',
              fontSize: '0.875rem',
            },
          }}
        >
          {rowsPerPageOptions.map((option) => (
            <MenuItem key={option} value={option} sx={{ fontSize: '0.875rem' }}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* Right side - Page info and navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
          {count === 0 ? '0 of 0' : `${startIndex}-${endIndex} of ${count}`}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            onClick={handleFirstPage}
            disabled={page === 0}
            size="small"
            sx={{ padding: '4px' }}
          >
            <FirstPage fontSize="small" />
          </IconButton>
          
          <IconButton
            onClick={handlePreviousPage}
            disabled={page === 0}
            size="small"
            sx={{ padding: '4px' }}
          >
            <ChevronLeft fontSize="small" />
          </IconButton>
          
          <IconButton
            onClick={handleNextPage}
            disabled={page >= totalPages - 1}
            size="small"
            sx={{ padding: '4px' }}
          >
            <ChevronRight fontSize="small" />
          </IconButton>
          
          <IconButton
            onClick={handleLastPage}
            disabled={page >= totalPages - 1}
            size="small"
            sx={{ padding: '4px' }}
          >
            <LastPage fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default CompactPagination;
